import React from 'react'
import { Header, Text, Button, Loader, Icon, HTMLText } from '@crossfit/ui'
import { format, parse } from 'date-fns'

import 'keen-slider/keen-slider.min.css'
import { useAuth } from 'components/Auth'
import { getDateRange } from 'utilities/dates'
import InnerContent from 'components/InnerContent'
import classNames from 'classnames'
import usePageTitle from 'hooks/usePageTitle'
import NotAuthorized from 'components/NotAuthorized'
import style from './style.module.css'
import useDashboard from './hooks'
import Carousel from 'components/Carousel'
import { useNavigate } from 'react-router-dom'
import { trackEvent } from 'services/segment'
import { useAffiliate } from 'hooks/useAffiliate'
import { Link } from 'react-router-dom'
import { Role } from 'types'

function DashboardPage() {
  const { content, isLoading, hasAccess, dailyClassPlan, rxWorkout } =
    useDashboard()
  const { user } = useAuth()
  const { selectedAffiliate: affiliate } = useAffiliate()
  const navigate = useNavigate()

  usePageTitle(
    `CrossFit Affiliate Toolkit${
      content ? ` | ${content.hero.affiliateIdText}` : ''
    }`,
  )

  function onCapWorkoutClick() {
    trackEvent({
      component: 'Dashboard Page',
      event: 'Dashboard Daily CAP Workout Clicked',
      location: 'Affiliate Toolkit',
      text: content.capWorkout.linkText,
      affiliateId: affiliate?.affiliateId,
    })
    navigate(dailyClassPlan.data.slug)
  }

  function formatDate(date: string) {
    const nndate = parse(date, 'yyyyMMdd', new Date())

    return format(nndate, 'MMMM d')
  }

  if (isLoading) {
    return <Loader />
  }

  if (!hasAccess) {
    return <NotAuthorized />
  }

  return (
    <>
      <div className={style.dashboardHeader}>
        <InnerContent variant='xxlarge' className={style.innerHeaderContent}>
          <div className={style.affiliateDetails}>
            <Header size='large' as='h1'>
              {user.firstName} {user.lastName}
            </Header>

            {affiliate?.name && (
              <Text size='xsmall' className={style.affiliateName}>
                {affiliate?.name}
              </Text>
            )}
            {affiliate?.id && (
              <Text size='xsmall' className={style.tag}>
                {content.hero.affiliateIdText + ' ' + affiliate?.id}
              </Text>
            )}
          </div>
          {content.hero.ctas.map(
            ({ headlineText, bodyText, link, icon, roleVisibility }) => {
              return roleVisibility
                .map((s) => s.split('_')[1])
                .some((s) => affiliate?.roles?.includes(s)) ||
                roleVisibility.includes('all') ? (
                <Link
                  to={
                    link.url.includes('/tools')
                      ? link.url.split('/tools/')[1]
                      : link.url
                  }
                >
                  <div className={style.heroCtas}>
                    <div>
                      <img src={icon} alt={headlineText} />
                    </div>
                    <div className={style.ctaContent}>
                      {headlineText.includes('&') &&
                        affiliate.roles.includes(Role.LOR) && (
                          <Header
                            as='h4'
                            size='xsmall'
                            className={style.ctaHeadlineText}
                          >
                            {headlineText}
                          </Header>
                        )}
                      {headlineText.includes('&') &&
                        !affiliate.roles.includes(Role.LOR) && (
                          <Header
                            as='h4'
                            size='xsmall'
                            className={style.ctaHeadlineText}
                          >
                            VIEW COACHES & STAFF
                          </Header>
                        )}
                      {!headlineText.includes('&') && (
                        <Header
                          as='h4'
                          size='xsmall'
                          className={style.ctaHeadlineText}
                        >
                          {headlineText}
                        </Header>
                      )}

                      <Text size='xsmall' className={style.ctaBodyText}>
                        {bodyText}
                      </Text>
                    </div>
                  </div>
                </Link>
              ) : null
            },
          )}
        </InnerContent>
      </div>
      <div className={style.dashboardContainer}>
        <InnerContent className={style.innerContent} as='main' variant='xlarge'>
          <div className={style.dashboardHighlightsContainer}>
            <Carousel
              slideId='keen-slider__slide-highlights'
              slidesPerView={1}
              numItems={2}
              arrowDisplayType='boxed-right'
            >
              {content.highlights.map(
                ({ headlineText, bodyText, link, image, roleVisibility }) => {
                  return roleVisibility
                    .map((s) => s.split('_')[1])
                    .some((s) => affiliate?.roles?.includes(s)) ||
                    roleVisibility.includes('all') ? (
                    <div
                      className={classNames(
                        style.highlightContainer,
                        `keen-slider__slide-highlights`,
                      )}
                      key={headlineText}
                    >
                      <img src={image?.sizes?.small} alt={headlineText} />
                      <div className={style.highlightContent}>
                        <Header
                          as='h4'
                          size='small'
                          className={style.highlightHeadlineText}
                        >
                          {headlineText}
                        </Header>
                        <Text size='small' className={style.bodyText}>
                          {bodyText}
                        </Text>
                        <Button
                          className={style.readMoreButton}
                          variant='tertiary'
                          as='a'
                          href={link.url}
                          rightIcon={
                            <Icon
                              className={style.readMoreButtonIcon}
                              name='arrow-right'
                            />
                          }
                        >
                          {link.text}
                        </Button>
                      </div>
                    </div>
                  ) : null
                },
              )}
            </Carousel>
          </div>
          <div className={style.columnWrapper}>
            <div className={style.leftColumn}>
              <div className={style.content}>
                <div className={style.cardsContainer}>
                  <div className={style.cardsList}>
                    <div className={style.cardContent}>
                      <div className={style.capHeader}>
                        <div className={style.tag}>CAP WORKOUT</div>
                        <div className={style.capTitle}>
                          {dailyClassPlan.data.date.toLocaleDateString(
                            'en-US',
                            {
                              weekday: 'long',
                            },
                          )}
                        </div>
                        <div className={style.capDate}>
                          {format(dailyClassPlan.data.date, 'yyMMdd')}
                        </div>
                      </div>
                    </div>
                    <div className={style.cardContent}>
                      <HTMLText>{rxWorkout.description}</HTMLText>
                    </div>
                    <Button
                      variant='secondary'
                      size='medium'
                      className={style.capButton}
                      as='a'
                      onClick={onCapWorkoutClick}
                    >
                      {content.capWorkout.linkText}
                    </Button>
                  </div>
                </div>
              </div>
              <div className={style.content}>
                <Carousel
                  slideId='keen-slider__slide-featured-content'
                  slidesPerView={1}
                  numItems={content.featuredContent.items.length}
                  arrowDisplayType='dots-beneath'
                >
                  {content.featuredContent.items.map(
                    ({ headlineText, image, bodyText, link }) => (
                      <div
                        className={classNames(
                          `keen-slider__slide-featured-content`,
                        )}
                        key={headlineText}
                      >
                        <div
                          className={style.featuredContentCard}
                          style={
                            image
                              ? {
                                  backgroundImage: `url(${image.sizes.large})`,
                                }
                              : undefined
                          }
                        >
                          <div className={style.featuredCardContent}>
                            <p className={style.featuredContentHeadlineText}>
                              {headlineText}
                            </p>
                            <p className={style.featuredContentDescriptionText}>
                              {bodyText}
                            </p>
                            <a
                              className={style.featuredContentLink}
                              href={link.url}
                              onClick={() => {
                                trackEvent({
                                  component: 'Dashboard Page',
                                  event: 'Dashboard Featured Content Clicked',
                                  text: headlineText,
                                  location: 'Affiliate Toolkit',
                                  linkUrl: link.url,
                                  affiliateId: affiliate?.affiliateId,
                                })
                              }}
                            >
                              {link.text.toUpperCase()}
                            </a>
                          </div>
                        </div>
                      </div>
                    ),
                  )}
                </Carousel>
              </div>
              <div className={style.content}>
                <div className={style.cardsContainer}>
                  <div className={style.cardsHeadlineTextContainer}>
                    <Header
                      as='h3'
                      size='small'
                      className={style.cardsHeadlineText}
                    >
                      {content.recentUpdates.headlineText}
                    </Header>
                  </div>
                  <div className={style.recentUpdates}>
                    <div className={style.cardsList}>
                      {content.recentUpdates.updates.map(
                        ({ headlineText, articleDate, link }) => (
                          <div className={style.cardContent} key={headlineText}>
                            <a href={link.url}>
                              <Text size='medium' className={style.bodyText}>
                                {headlineText}
                              </Text>
                            </a>
                            <Text size='xsmall' className={style.dateText}>
                              {formatDate(articleDate)}
                            </Text>
                          </div>
                        ),
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className={style.rightColumn}>
              <div className={style.content}>
                <div className={style.cardsContainer}>
                  <div className={style.cardsHeadlineTextContainer}>
                    <Header
                      as='h3'
                      size='small'
                      className={style.cardsHeadlineText}
                    >
                      {content.upcomingEvents.headlineText}
                    </Header>
                  </div>
                  <div className={style.cardsList}>
                    {content.upcomingEvents.affiliateEvents.map(
                      ({
                        headlineText,
                        locationText,
                        type,
                        startDateTime,
                        endDateTime,
                        cta,
                      }) => (
                        <div className={style.cardContent} key={headlineText}>
                          <div className={style.tag}>{type}</div>
                          <Text size='large' className={style.eventTitle}>
                            {headlineText}
                          </Text>
                          <div className={style.eventDetails}>
                            <Text size='small' className={style.eventDate}>
                              {getDateRange(startDateTime, endDateTime)}
                            </Text>
                            <Text size='small' className={style.location}>
                              {locationText}
                            </Text>
                          </div>
                          {cta && (
                            <div className={style.cta}>
                              <Button
                                as='a'
                                variant='tertiary'
                                href={cta.url}
                                target='_blank'
                                rel='noreferrer'
                              >
                                {cta.text}
                              </Button>
                            </div>
                          )}
                        </div>
                      ),
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </InnerContent>
      </div>
    </>
  )
}

export default DashboardPage
